import React, { useMemo } from 'react';
import { Stack, Typography } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { EncoreFormRenderer } from '@locus-taxy/encore-form';
import type { FormRendererRef } from '@locus-taxy/encore-form';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';
import type { ProfileViewMode, ViewField } from './profileTypes';
import { ProfileViewRenderer } from './ProfileViewRenderer';
import { buildPersonalInfoSchema } from './profileFormSchemas';

interface PersonalInfoSectionProps {
  readonly mode: ProfileViewMode;
  /** The normalised profile blocks, keyed as the form schema keys them. */
  readonly formInitialData: Record<string, unknown>;
  readonly clientId: string;
  readonly formRef: React.RefObject<FormRendererRef | null>;
  readonly onChange?: (data: Record<string, unknown>) => void;
  readonly viewFields: ViewField[];
  /** The person entity's gender values, as the backend serves them. */
  readonly genderOptions: readonly string[];
}

export function PersonalInfoSection({
  mode,
  formInitialData,
  clientId,
  formRef,
  onChange,
  viewFields,
  genderOptions,
}: PersonalInfoSectionProps) {
  const { t } = useTranslation();

  const schema = useMemo(
    () => buildPersonalInfoSchema(t, genderOptions),
    [t, genderOptions]
  );
  const additionalContext = useMemo(
    () => ({ profile: formInitialData }),
    [formInitialData]
  );

  return (
    <div
      className="rounded-md border border-border bg-card"
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'personal-info-section',
        page: 'account',
      })}
    >
      <Stack gap={3} className="p-8">
        <Typography variant="h6" fontWeight="semibold">
          {t(
            'dashboard.userDetails.personalInformation',
            'Personal Information'
          )}
        </Typography>

        {mode === 'view' ? (
          <ProfileViewRenderer fields={viewFields} columns={2} />
        ) : (
          <EncoreFormRenderer
            ref={formRef}
            schema={schema as any}
            clientId={clientId}
            initialData={formInitialData}
            additionalContext={additionalContext}
            onChange={onChange}
            routeContext={{ page: 'profile' }}
          />
        )}
      </Stack>
    </div>
  );
}
